import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Button,
  Drawer,
  ListItemButton,
  ListItemIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Alert,
  CircularProgress,
  Badge,
  Snackbar
} from '@mui/material';
import JobApplications from './JobApplications';

import {
  Work as WorkIcon,
  Person as PersonIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  RocketLaunch as BoostIcon,
  Analytics as AnalyticsIcon,
  Notifications as NotificationsIcon,
  Dashboard as DashboardIcon
} from '@mui/icons-material';
  import { useNavigate } from 'react-router-dom';
  import { useAuth } from '../../context/AuthContext';
  import ApiService from '../../services/api';
  import DatabaseService from '../../services/DatabaseService';
  import { calculateEmployerCompletion } from '../../utils/profileCompletion';
import JobBoostPayment from '../../components/jobs/JobBoostPayment';
import ProfileBoostPayment from '../../components/profile/ProfileBoostPayment';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement
);

  const Dashboard = () => {
    const navigate = useNavigate();
  const { currentUser, isAdmin, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [selectedView, setSelectedView] = useState(0);
  
  // Sidebar configuration
  const drawerWidth = 240;
  const menuItems = [
    { text: 'Overview', icon: <DashboardIcon />, value: 0 },
    { text: 'Job Management', icon: <WorkIcon />, value: 1 },
    { text: 'Job Applications', icon: <PersonIcon />, value: 2 },
    { text: 'Analysis', icon: <AnalyticsIcon />, value: 3 }
  ];
  
  // Data states
  const [stats, setStats] = useState({
    activeJobs: 0,
    totalApplications: 0,
    viewsToday: 0,
    responseRate: 0,
    totalViews: 0,
    hiredCandidates: 0
  });
  
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [profileCompletion, setProfileCompletion] = useState(0);
  // Service and booking data removed
  const [notifications, setNotifications] = useState([]);
  const [analytics, setAnalytics] = useState({
    applicationTrends: [],
    jobPerformance: [],
    topSkills: []
  });

  // Dialog states
  const [applicationDialogOpen, setApplicationDialogOpen] = useState(false);
  // Booking dialog removed
  const [selectedApplication, setSelectedApplication] = useState(null);
  // Selected booking removed
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // generate analytics first to avoid use-before-define
  const generateAnalytics = useCallback((jobsData, applicationsData) => {
    const applicationTrends = Array.from({ length: 7 }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dayApplications = applicationsData.filter(app => {
        const appDate = new Date(app.createdAt);
        return appDate.toDateString() === date.toDateString();
      }).length;
      return {
        date: date.toLocaleDateString('en-US', { weekday: 'short' }),
        applications: dayApplications
      };
    }).reverse();

    const jobPerformance = jobsData.slice(0, 5).map(job => ({
      title: job.title.length > 20 ? job.title.substring(0, 20) + '...' : job.title,
      applications: job.applicationsCount || 0,
      views: job.views || 0
    }));

    const topSkills = [
      { skill: 'React', count: 15 },
      { skill: 'JavaScript', count: 12 },
      { skill: 'Node.js', count: 8 },
      { skill: 'Python', count: 6 },
      { skill: 'TypeScript', count: 5 }
    ];

    setAnalytics({ applicationTrends, jobPerformance, topSkills });
  }, []);

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Load jobs for current employer using the secure my-jobs endpoint
      const jobsResponse = await ApiService.getMyJobs({ 
        limit: 100 
      });
      // Normalize response shape: support {jobs}, {data: {jobs}}, or array
      const jobsData = Array.isArray(jobsResponse)
        ? jobsResponse
        : (jobsResponse.jobs 
          || jobsResponse.data?.jobs 
          || jobsResponse.data 
          || []);
      setJobs(jobsData);
      
      // Load applications for employer's jobs
      const applicationsResponse = await ApiService.getApplications({ 
        employerId: currentUser.uid,
        limit: 100 
      });
      const applicationsData = applicationsResponse.applications || [];
      setApplications(applicationsData);
      
      // Load notifications (enhanced with service notifications)
      const notificationsData = [
        {
          id: 1,
          message: 'New application received for Senior React Developer',
          timestamp: new Date(),
          read: false,
          type: 'job'
        },
        {
          id: 2,
          message: 'Job posting "Frontend Developer" expires in 3 days',
          timestamp: new Date(Date.now() - 86400000),
          read: false,
          type: 'job'
        }
      ];
      setNotifications(notificationsData);

      // Load employer profile and compute completion
      try {
        const uid = currentUser.uid || currentUser._id;
        const userData = await DatabaseService.getUserProfile(uid);
        const profile = userData.profileData || userData.employerProfile || userData.profile || {};
        const completion = calculateEmployerCompletion(profile);
        setProfileCompletion(completion.percent);
      } catch (e) {
        console.warn('Failed to load employer profile for completion:', e);
      }
      
      // Calculate stats
      // Treat both 'approved' and legacy 'active' as active listings
      const activeJobs = jobsData.filter(job => ['approved','active'].includes(job.status)).length;
      const totalApplications = applicationsData.length;
      const totalViews = jobsData.reduce((sum, job) => sum + (job.views || 0), 0);
      const hiredCandidates = applicationsData.filter(app => app.status === 'hired').length;
      const responseRate = totalApplications > 0 ? 
        Math.round((applicationsData.filter(app => app.status !== 'pending').length / totalApplications) * 100) : 0;
      
      setStats({
        activeJobs,
        totalApplications,
        viewsToday: Math.floor(totalViews * 0.1), // Simulate today's views
        responseRate,
        totalViews,
        hiredCandidates
      });
      
      // Generate analytics data
      generateAnalytics(jobsData, applicationsData);
      
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUser, generateAnalytics]);

  // Load dashboard data (declare after loadDashboardData to satisfy linter)
  useEffect(() => {
    if (currentUser) {
      loadDashboardData();
    }
  }, [currentUser, loadDashboardData]);

  const [systemSettings, setSystemSettings] = useState(null);
  const [jobBoostPaymentOpen, setJobBoostPaymentOpen] = useState(false);
  const [profileBoostOpen, setProfileBoostOpen] = useState(false);
  const [jobForBoost, setJobForBoost] = useState(null);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settings = await ApiService.getSystemSettings();
        setSystemSettings(settings);
      } catch (e) {
        console.warn('Failed to load system settings:', e);
      }
    };
    loadSettings();
  }, []);

  const handleBoostJob = async (jobId) => {
    try {
      const isAdminSession = !!localStorage.getItem('adminToken');
      if (isAdminSession) {
        await ApiService.boostJob(jobId, {});
        setSnackbar && setSnackbar({ open: true, message: 'Job boosted successfully (admin override)', severity: 'success' });
        const jobsRes = await ApiService.getMyJobs({ limit: 100 });
        setJobs(jobsRes.jobs || jobsRes.data?.jobs || []);
        return;
      }

      const requirePayment = !!systemSettings?.payments?.enableBoostPayments;
      if (requirePayment) {
        setJobForBoost(jobId);
        setJobBoostPaymentOpen(true);
        return;
      }

      await ApiService.requestJobBoost(jobId);
      setSnackbar && setSnackbar({ open: true, message: 'Boost request submitted. Admin will approve shortly.', severity: 'success' });
      const jobsRes = await ApiService.getMyJobs({ limit: 100 });
      setJobs(jobsRes.jobs || jobsRes.data?.jobs || []);
    } catch (error) {
      console.error('Error boosting job:', error);
      const msg = error?.message || 'Failed to boost job';
      setSnackbar && setSnackbar({ open: true, message: msg, severity: 'error' });
    }
  };

  const handleJobBoostPaid = async () => {
    try {
      if (!jobForBoost) return;
      await ApiService.requestJobBoost(jobForBoost);
      setSnackbar && setSnackbar({ open: true, message: 'Payment recorded and boost request submitted.', severity: 'success' });
      setJobBoostPaymentOpen(false);
      setJobForBoost(null);
      const jobsRes = await ApiService.getMyJobs({ limit: 100 });
      setJobs(jobsRes.jobs || jobsRes.data?.jobs || []);
    } catch (error) {
      console.error('Error after payment submitting boost request:', error);
      setSnackbar && setSnackbar({ open: true, message: 'Payment succeeded but boost request failed. Please contact support.', severity: 'error' });
    }
  };

  const handleBoostProfile = async () => {
    try {
      const uid = currentUser?.uid || currentUser?._id;
      const isAdminSession = !!isAdmin;
      // Require a USER session for boost request when not using admin override
      if (!isAdminSession && !token) {
        setSnackbar && setSnackbar({ open: true, message: 'Please log in to boost your profile.', severity: 'warning' });
        return;
      }
      if (isAdminSession) {
        await ApiService.boostUserProfile(uid, { override: true });
        setSnackbar && setSnackbar({ open: true, message: 'Profile boosted successfully (admin override)', severity: 'success' });
        return;
      }

      const requirePayment = !!systemSettings?.payments?.enableBoostPayments;
      if (requirePayment) {
        setProfileBoostOpen(true);
        return;
      }

      await ApiService.requestUserBoost(uid);
      setSnackbar && setSnackbar({ open: true, message: 'Boost request submitted. Admin will approve shortly.', severity: 'success' });
      } catch (error) {
        console.error('Error boosting profile:', error);
        setSnackbar && setSnackbar({ open: true, message: 'Failed to boost profile', severity: 'error' });
      }
    };

  const handleProfileBoostPaid = async (transaction) => {
    try {
      const uid = currentUser?.uid || currentUser?._id;
      await ApiService.requestUserBoost(uid, { paymentTransactionId: transaction?.transactionId });
      setSnackbar && setSnackbar({ open: true, message: 'Payment recorded and boost request submitted.', severity: 'success' });
      setProfileBoostOpen(false);
      const jobsRes = await ApiService.getMyJobs({ limit: 100 });
      setJobs(jobsRes.jobs || jobsRes.data?.jobs || []);
    } catch (error) {
      console.error('Error finalizing profile boost after payment:', error);
      setSnackbar && setSnackbar({ open: true, message: 'Payment succeeded but boost request failed. Please contact support.', severity: 'error' });
    }
  };

  

  const handleJobAction = async (action, jobId) => {
    try {
      switch (action) {
        case 'edit':
          navigate(`/employer/jobs/edit/${jobId}`);
          break;
        case 'delete':
          await ApiService.deleteJob(jobId);
          loadDashboardData();
          break;
        case 'toggle':
          const job = jobs.find(j => j.id === jobId);
          await ApiService.updateJob(jobId, {
            status: job.status === 'active' ? 'paused' : 'active'
          });
          loadDashboardData();
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Error handling job action:', error);
    }
  };


  const handleApplicationAction = async (action, applicationId, newStatus) => {
    try {
      await ApiService.updateApplicationStatus(applicationId, newStatus);
      loadDashboardData();
      setApplicationDialogOpen(false);
    } catch (error) {
      console.error('Error updating application:', error);
    }
  };


  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'approved': return 'success';
      case 'paused': return 'warning';
      case 'closed': return 'error';
      case 'pending': return 'info';
      case 'expired': return 'warning';
      case 'removed': return 'error';
      case 'draft': return 'default';
      case 'hired': return 'success';
      case 'rejected': return 'error';
      case 'interviewed': return 'warning';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      case 'confirmed': return 'info';
      default: return 'default';
    }
  };

  // Chart configurations
  const applicationTrendsChart = {
    labels: analytics.applicationTrends.map(item => item.date),
    datasets: [
      {
        label: 'Applications',
        data: analytics.applicationTrends.map(item => item.applications),
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        tension: 0.1
      }
    ]
  };

  const jobPerformanceChart = {
    labels: analytics.jobPerformance.map(item => item.title),
    datasets: [
      {
        label: 'Applications',
        data: analytics.jobPerformance.map(item => item.applications),
        backgroundColor: '#cf1130',
      },
      {
        label: 'Views',
        data: analytics.jobPerformance.map(item => item.views),
        backgroundColor: 'rgba(255, 99, 132, 0.8)',
      }
    ]
  };


  const topSkillsChart = {
    labels: analytics.topSkills.map(item => item.skill),
    datasets: [
      {
        data: analytics.topSkills.map(item => item.count),
        backgroundColor: [
          '#FF6384',
          '#cf1130',
          '#FFCE56',
          '#4BC0C0',
          '#9966FF'
        ]
      }
    ]
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Sidebar */}
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            top: '64px', // Adjust based on your app bar height
            height: 'calc(100vh - 64px)',
          },
        }}
      >
        <Box sx={{ overflow: 'auto' }}>
          <List>
            {menuItems.map((item) => (
              <ListItemButton
                key={item.value}
                selected={selectedView === item.value}
                onClick={() => setSelectedView(item.value)}
                sx={{
                  minHeight: 48,
                  justifyContent: 'initial',
                  px: 2.5,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: 3,
                    justifyContent: 'center',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: 'background.default',
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: '64px', // Add top margin for fixed header
        }}
      >
        <Container maxWidth="lg" sx={{ mt: 1, mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" gutterBottom>
              Employer Dashboard
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/employer/jobs/create')}
              >
                Post New Job
              </Button>
            </Box>
          </Box>

          {/* Profile Completion Alert */}
          {profileCompletion < 100 && (
            <Alert severity="info" sx={{ mb: 3 }} action={
              <Button color="inherit" size="small" onClick={() => navigate('/employer/profile', { state: { openEdit: true } })}>
                Complete your profile
              </Button>
            }>
              Your company profile is {profileCompletion}% complete. Complete it to build trust and improve job performance!
            </Alert>
          )}

      {/* Enhanced Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                  <WorkIcon />
                </Avatar>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Active Jobs
                  </Typography>
                  <Typography variant="h5">{stats.activeJobs}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>


        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                  <PersonIcon />
                </Avatar>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Applications
                  </Typography>
                  <Typography variant="h5">{stats.totalApplications}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>



        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: 'error.main', mr: 2 }}>
                  <Badge badgeContent={notifications.filter(n => !n.read).length} color="error">
                    <NotificationsIcon />
                  </Badge>
                </Avatar>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Notifications
                  </Typography>
                  <Typography variant="h5">{notifications.length}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content Sections */}
      <Card>
        {/* Overview Tab */}
        {selectedView === 0 && (
          <CardContent>
            <Grid container spacing={3}>
              {/* Recent Applications */}
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Recent Applications
                </Typography>
                <List>
                  {applications.slice(0, 3).map((application) => (
                    <ListItem
                      key={application.id}
                      secondaryAction={
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            setSelectedApplication(application);
                            setApplicationDialogOpen(true);
                          }}
                        >
                          View
                        </Button>
                      }
                    >
                      <ListItemAvatar>
                        <Avatar>
                          <PersonIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={application.candidateName || 'Anonymous'}
                        secondary={
                          <>
                            <Typography component="span" variant="body2" color="text.primary">
                              {application.jobTitle}
                            </Typography>
                            {` — ${new Date(application.appliedAt?.toDate?.() || application.appliedAt).toLocaleDateString()}`}
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Grid>


              {/* Quick Actions */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/employer/jobs/create')}
                >
                  Post New Job
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<PersonIcon />}
                  onClick={() => setSelectedView(2)}
                >
                  Review Applications
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  fullWidth
                  variant="contained"
                  color="secondary"
                  startIcon={<BoostIcon />}
                  onClick={handleBoostProfile}
                >
                  Boost Profile
                </Button>
              </Grid>
            </Grid>
          </Grid>
          </Grid>
          </CardContent>
        )}

        <ProfileBoostPayment
          open={profileBoostOpen}
          onClose={() => setProfileBoostOpen(false)}
          userId={currentUser?._id || currentUser?.uid}
          isEmployer={true}
          settings={systemSettings}
          onPaid={handleProfileBoostPaid}
        />

        {/* Job Management Tab */}
        {selectedView === 1 && (
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Job Postings ({jobs.length})
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/employer/jobs/create')}
              >
                Post New Job
              </Button>
            </Box>
            
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Job Title</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Applications</TableCell>
                    <TableCell>Views</TableCell>
                    <TableCell>Posted Date</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {jobs.map((job) => (
                    <TableRow key={job.id}>
                      <TableCell>
                        <Typography variant="subtitle2">{job.title}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {typeof job.location === 'object' 
                            ? `${job.location.city || ''}, ${job.location.country || ''}`.replace(/^,\s*|,\s*$/g, '') 
                            : job.location || 'N/A'} • {job.type}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={job.status}
                          color={getStatusColor(job.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{job.applicationsCount || 0}</TableCell>
                      <TableCell>{job.views || 0}</TableCell>
                      <TableCell>
                        {new Date(job.createdAt?.toDate?.() || job.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/employer/jobs/${job.id}`)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        {(['approved','active'].includes(job.status)) && (
                          <Tooltip title="Boost Job">
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => handleBoostJob(job.id)}
                            >
                              <BoostIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Edit Job">
                          <IconButton
                            size="small"
                            onClick={() => handleJobAction('edit', job.id)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={job.status === 'active' ? 'Pause Job' : 'Activate Job'}>
                          <IconButton
                            size="small"
                            onClick={() => handleJobAction('toggle', job.id)}
                          >
                            {job.status === 'active' ? <CancelIcon /> : <CheckCircleIcon />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Job">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleJobAction('delete', job.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        )}

        {/* Service Management Tab replaced with Job Applications */}
        {selectedView === 2 && (
          <JobApplications />
        )}

        {/* Job Applications Tab */}
        {selectedView === 3 && (
          <JobApplications />
        )}


        {/* Analytics Tab */}
        {selectedView === 3 && (
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Analytics & Insights
            </Typography>
            
            <Grid container spacing={3}>
              {/* Application Trends */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Application Trends (Last 7 Days)
                    </Typography>
                    <Box sx={{ height: 300 }}>
                      <Line data={applicationTrendsChart} options={{ maintainAspectRatio: false }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>


              {/* Job Performance */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Job Performance
                    </Typography>
                    <Box sx={{ height: 300 }}>
                      <Bar data={jobPerformanceChart} options={{ maintainAspectRatio: false }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>


              {/* Top Skills */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Top Skills in Applications
                    </Typography>
                    <Box sx={{ height: 300 }}>
                      <Doughnut data={topSkillsChart} options={{ maintainAspectRatio: false }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Performance Metrics */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Performance Metrics
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemText
                          primary="Average Applications per Job"
                          secondary={jobs.length > 0 ? Math.round(stats.totalApplications / jobs.length) : 0}
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </CardContent>
        )}

        {/* Job Boost Payment Dialog */}
        <JobBoostPayment
          open={jobBoostPaymentOpen}
          onClose={() => { setJobBoostPaymentOpen(false); setJobForBoost(null); }}
          jobId={jobForBoost}
          settings={systemSettings}
          onPaid={handleJobBoostPaid}
        />
      </Card>

      {/* Application Review Dialog */}
      <Dialog
        open={applicationDialogOpen}
        onClose={() => setApplicationDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Review Application
        </DialogTitle>
        <DialogContent>
          {selectedApplication && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedApplication.candidateName || 'Anonymous'}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Applied for: {selectedApplication.jobTitle}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Applied on: {new Date(selectedApplication.appliedAt?.toDate?.() || selectedApplication.appliedAt).toLocaleDateString()}
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle1" gutterBottom>
                Cover Letter:
              </Typography>
              <Typography variant="body2" paragraph>
                {selectedApplication.coverLetter || 'No cover letter provided.'}
              </Typography>
              
              <Typography variant="subtitle1" gutterBottom>
                Current Status:
              </Typography>
              <Chip
                label={selectedApplication.status}
                color={getStatusColor(selectedApplication.status)}
                sx={{ mb: 2 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplicationDialogOpen(false)}>
            Close
          </Button>
          <Button
            color="error"
            onClick={() => handleApplicationAction('update', selectedApplication?.id, 'rejected')}
          >
            Reject
          </Button>
          <Button
            color="warning"
            onClick={() => handleApplicationAction('update', selectedApplication?.id, 'interviewed')}
          >
            Interview
          </Button>
          <Button
            color="success"
            onClick={() => handleApplicationAction('update', selectedApplication?.id, 'hired')}
          >
            Hire
          </Button>
        </DialogActions>
      </Dialog>

      {/* Booking Management Dialog removed */}
      {/* Snackbar for boost and actions feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
        </Container>
      </Box>
    </Box>
  );
};

export default Dashboard;